import { useEffect, useState } from 'react';
import { CreditCard, Phone } from 'lucide-react';

const directionLabel = (code?: string): string => {
  if (code === 'tbank_c2c') return 'Т-Банк C2C';
  if (code === 'tbank_sbp') return 'Т-Банк СБП';
  if (code === 'interbank_c2c') return 'Межбанк C2C';
  if (code === 'interbank_sbp') return 'Межбанк СБП';
  return '—';
};

const isC2C = (code?: string) => code === 'tbank_c2c' || code === 'interbank_c2c';
const isSBP = (code?: string) => code === 'tbank_sbp' || code === 'interbank_sbp';

/** Остаток до дедлайна AirEX (как в списке заявок ЛК). */
const formatDeadlineRemaining = (deadlineAt: string | null | undefined, now: Date): string => {
  if (!deadlineAt) return '—';
  try {
    const end = new Date(deadlineAt).getTime();
    if (Number.isNaN(end)) return '—';
    const diff = Math.max(0, end - now.getTime());
    if (diff === 0) return 'Истекло';
    const min = Math.floor(diff / 60000);
    const h = Math.floor(min / 60);
    if (h >= 1) return `${h} ч ${min % 60} мин`;
    return `${min} мин`;
  } catch {
    return '—';
  }
};

interface AirEXRequest {
  id: number;
  order_id: string;
  amount: number;
  payed_amount?: number;
  payed_amount_counterparty?: number;
  bank_name?: string;
  direction_code?: string;
  max_matches?: number;
  exchange_rate?: number;
  deadline_at?: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  request: AirEXRequest | null;
  /** Оставшееся количество мэтчей (если есть — показывается строка «Доступ. мэтчи») */
  remainingMatches?: number | null;
  /** Курс с наценкой контрагента (как в «Мои заявки»). Если передан — показывается вместо request.exchange_rate */
  displayRate?: number | null;
  /** Запланированная сумма мэтча (частичный take из подбора), если не на весь остаток */
  plannedMatchRub?: number | null;
}

const emitToast = (message: string, type: 'success' | 'error') => {
  window.dispatchEvent(new CustomEvent('showToast', { detail: { message, type } }));
};

export default function ConfirmTakeWidget({ isOpen, onClose, onConfirm, request, remainingMatches, displayRate, plannedMatchRub }: Props) {
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!isOpen || !request?.deadline_at) return;
    setNow(new Date());
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [isOpen, request?.deadline_at]);

  if (!isOpen || !request) return null;
  const remaining = request.amount - (request.payed_amount || 0) - (request.payed_amount_counterparty || 0);
  const showPlanned =
    plannedMatchRub != null &&
    plannedMatchRub > 0 &&
    Math.abs(plannedMatchRub - remaining) > 0.01;
  const showRemainingMatches = (request.max_matches ?? 0) > 0 && (remainingMatches != null || request.max_matches != null);
  const remainingMatchesValue = remainingMatches ?? request.max_matches ?? 0;
  const deadlineExpired =
    Boolean(request.deadline_at) && new Date(request.deadline_at as string).getTime() <= now.getTime();

  const copyId = () => {
    navigator.clipboard.writeText(request.order_id)
      .then(() => emitToast('ID скопирован', 'success'))
      .catch(() => emitToast('Не удалось скопировать', 'error'));
  };

  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await Promise.resolve(onConfirm());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-[200]">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700 shadow-2xl">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Подтверждение</h3>
        <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
          Взять в работу заявку{' '}
          <button
            type="button"
            onClick={copyId}
            className="font-semibold underline cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 bg-transparent p-0 border-0 appearance-none align-baseline"
            title={`${request.order_id} (клик — скопировать)`}
          >
            #{request.order_id}
          </button>
          {' '}и отработать её?
        </p>
        <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-4 mb-5 border border-gray-200/70 dark:border-gray-700/60">
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
            <div className="flex items-baseline justify-between gap-4">
              <span className="font-semibold text-gray-700 dark:text-gray-200">Остаток</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {remaining.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
              </span>
            </div>
            {request.deadline_at ? (
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-semibold text-gray-700 dark:text-gray-200">Осталось</span>
                <span
                  className={`font-semibold tabular-nums ${
                    deadlineExpired
                      ? 'text-rose-600 dark:text-rose-400'
                      : 'text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {formatDeadlineRemaining(request.deadline_at, now)}
                </span>
              </div>
            ) : null}
            {showPlanned && plannedMatchRub != null && (
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-semibold text-gray-700 dark:text-gray-200">Мэтч на</span>
                <span className="font-semibold text-indigo-700 dark:text-indigo-300">
                  {plannedMatchRub.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
                </span>
              </div>
            )}
            {showRemainingMatches && (
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-semibold text-gray-700 dark:text-gray-200">Доступ. мэтчи</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{remainingMatchesValue}</span>
              </div>
            )}
            {request.bank_name && (
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-semibold text-gray-700 dark:text-gray-200">Банк</span>
                <span className="text-gray-900 dark:text-gray-100">{request.bank_name}</span>
              </div>
            )}
            <div className="flex items-baseline justify-between gap-4">
              <span className="font-semibold text-gray-700 dark:text-gray-200">Метод</span>
              <span className="text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                {isC2C(request.direction_code) && <span title="C2C"><CreditCard className="shrink-0 w-4 h-4 text-blue-600 dark:text-blue-400" /></span>}
                {isSBP(request.direction_code) && <span title="СБП"><Phone className="shrink-0 w-4 h-4 text-blue-600 dark:text-blue-400" /></span>}
                {directionLabel(request.direction_code)}
              </span>
            </div>
            {((displayRate != null && displayRate > 0) || (request.exchange_rate != null && request.exchange_rate > 0)) && (
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-semibold text-gray-700 dark:text-gray-200">Курс</span>
                <span className="font-semibold text-blue-700 dark:text-blue-400">
                  {(displayRate ?? request.exchange_rate ?? 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-xl disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
          >
            {loading ? 'Отправка…' : 'Да, взять'}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 py-2.5 px-4 rounded-xl text-gray-800 dark:text-gray-200">
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
